import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { apiClient, getErrorMessage } from '../../api/client'
import { useOfflineFirstQuery } from '../../hooks/useOfflineFirstQuery'
import { resolveTenantSlug } from '../../lib/tenant'

type PaginatedResponse<T> = {
  count: number
  next: string | null
  previous: string | null
  results: T[]
}

type AuthorizationListItem = {
  id: number
  authorization_number: string
  client: number
  client_name: string
  service_code: string
  start_date: string
  end_date: string
  authorized_units: string
  used_units: string
  pending_units: string
  available_units: string
  utilization_percentage: number
  status: string
  is_active: boolean
}

const FILTER_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'expiring', label: 'Expiring soon' },
  { value: 'low_units', label: 'Low units' },
]

export default function AuthorizationsPage() {
  const tenantSlug = resolveTenantSlug()
  const [searchParams, setSearchParams] = useSearchParams()
  const [filter, setFilter] = useState(searchParams.get('filter') ?? '')
  const [client, setClient] = useState(searchParams.get('client') ?? '')

  const params = useMemo(() => {
    const p: Record<string, string> = {}
    if (filter) p.filter = filter
    if (client) p.client = client
    return p
  }, [client, filter])

  const cacheKey = useMemo(() => {
    return `tenant:${tenantSlug ?? 'unknown'}:authorizations:list:filter=${filter || 'all'}:client=${client || 'all'}`
  }, [client, filter, tenantSlug])

  const query = useOfflineFirstQuery<PaginatedResponse<AuthorizationListItem>>({
    queryKey: ['authorizations', params],
    cacheKey,
    cacheMaxAgeMs: 10 * 60 * 1000,
    queryFn: async () => {
      const res = await apiClient.get<PaginatedResponse<AuthorizationListItem>>('/api/v1/authorizations/', {
        params,
      })
      return res.data
    },
  })

  const rows = query.data?.results ?? []

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Authorizations</h1>
          <p className="text-sm text-gray-600">Utilization ledger enforces hard stops and auditability.</p>
        </div>
      </div>

      <div className="card p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="label" htmlFor="filter">
              Filter
            </label>
            <select
              id="filter"
              className="input mt-1"
              value={filter}
              onChange={(e) => {
                const v = e.target.value
                setFilter(v)
                const next = new URLSearchParams(searchParams)
                if (v) next.set('filter', v)
                else next.delete('filter')
                setSearchParams(next, { replace: true })
              }}
            >
              {FILTER_OPTIONS.map((o) => (
                <option key={o.value || 'all'} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label" htmlFor="client">
              Client ID
            </label>
            <input
              id="client"
              className="input mt-1"
              value={client}
              onChange={(e) => {
                const v = e.target.value
                setClient(v)
                const next = new URLSearchParams(searchParams)
                if (v) next.set('client', v)
                else next.delete('client')
                setSearchParams(next, { replace: true })
              }}
              placeholder="Filter by client id"
            />
          </div>

          <div className="flex items-end">
            <button
              type="button"
              className="btn-secondary w-full"
              onClick={() => {
                setFilter('')
                setClient('')
                setSearchParams(new URLSearchParams(), { replace: true })
              }}
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {query.isError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {getErrorMessage(query.error)}
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="table-header">Authorization</th>
                <th className="table-header">Client</th>
                <th className="table-header">Service</th>
                <th className="table-header">Dates</th>
                <th className="table-header">Available</th>
                <th className="table-header">Used</th>
                <th className="table-header">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {query.isLoading && rows.length === 0 ? (
                Array.from({ length: 10 }).map((_, idx) => (
                  <tr key={idx}>
                    <td className="table-cell"><div className="h-4 w-36 bg-gray-200 rounded animate-pulse" /></td>
                    <td className="table-cell"><div className="h-4 w-44 bg-gray-200 rounded animate-pulse" /></td>
                    <td className="table-cell"><div className="h-4 w-16 bg-gray-200 rounded animate-pulse" /></td>
                    <td className="table-cell"><div className="h-4 w-40 bg-gray-200 rounded animate-pulse" /></td>
                    <td className="table-cell"><div className="h-4 w-20 bg-gray-200 rounded animate-pulse" /></td>
                    <td className="table-cell"><div className="h-4 w-20 bg-gray-200 rounded animate-pulse" /></td>
                    <td className="table-cell"><div className="h-4 w-24 bg-gray-200 rounded animate-pulse" /></td>
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="table-cell text-gray-500">
                    No authorizations found.
                  </td>
                </tr>
              ) : (
                rows.map((a) => (
                  <tr key={a.id} className="hover:bg-gray-50">
                    <td className="table-cell">
                      <Link to={`/app/authorizations/${a.id}`} className="font-medium text-gray-900 hover:text-primary-600">
                        {a.authorization_number}
                      </Link>
                    </td>
                    <td className="table-cell">
                      <Link to={`/app/clients/${a.client}`} className="text-gray-900 hover:text-primary-600">
                        {a.client_name}
                      </Link>
                    </td>
                    <td className="table-cell">{a.service_code}</td>
                    <td className="table-cell">
                      {a.start_date} - {a.end_date}
                    </td>
                    <td className="table-cell">{a.available_units}</td>
                    <td className="table-cell">{a.used_units}</td>
                    <td className="table-cell">{a.status}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

